import { isPlatformBrowser } from "@angular/common";
import {
  AfterViewInit,
  OnDestroy,
  OnInit,
  TemplateRef,
  Component,
  HostListener,
  Inject,
  PLATFORM_ID,
  ViewChild,
  ChangeDetectorRef
} from "@angular/core";
import { FormArray, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router, NavigationEnd } from "@angular/router";
import { AppActionTypes } from "@app/store/actions/app.actions";
import { SetBreadcrumb } from "@app/store/actions/breadcrumb.actions";
import { SaveImage } from "@app/store/actions/image.actions";
import { LoadThumbnail } from "@app/store/actions/thumbnail.actions";
import { MainState } from "@app/store/state";
import { ImageAlias } from "@core/enums/image-alias.enum";
import { DeepSkyAcquisitionInterface } from "@core/interfaces/deep-sky-acquisition.interface";
import { SubjectType, SolarSystemSubjectType } from "@core/interfaces/image.interface";
import { SolarSystemAcquisitionInterface } from "@core/interfaces/solar-system-acquisition.interface";
import { ImageApiService } from "@core/services/api/classic/images/image/image-api.service";
import { RemoteSourceAffiliateApiService } from "@core/services/api/classic/remote-source-affiliation/remote-source-affiliate-api.service";
import { ClassicRoutesService } from "@core/services/classic-routes.service";
import { DeviceService } from "@core/services/device.service";
import { FooterVisibilityService } from "@core/services/footer-visibility.service";
import { ComponentCanDeactivate } from "@core/services/guards/pending-changes-guard.service";
import { ImageDataCopyService } from "@core/services/image-data-copy.service";
import { LoadingService } from "@core/services/loading.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { TitleService } from "@core/services/title/title.service";
import { UserService } from "@core/services/user.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { LoadUser } from "@features/account/store/auth.actions";
import { selectUser } from "@features/account/store/auth.selectors";
import {
  LoadEquipmentItemFailure,
  EquipmentActionTypes,
  FindEquipmentPresets,
  ItemBrowserSet,
  LoadEquipmentItem,
  LoadEquipmentItemSuccess
} from "@features/equipment/store/equipment.actions";
import { selectEquipmentItem, selectEquipmentPresets } from "@features/equipment/store/equipment.selectors";
import { EquipmentItemType, EquipmentItemUsageType } from "@features/equipment/types/equipment-item-base.interface";
import { EquipmentItem } from "@features/equipment/types/equipment-item.type";
import { EquipmentPresetInterface } from "@features/equipment/types/equipment-preset.interface";
import { FilterInterface } from "@features/equipment/types/filter.interface";
import { CopyAcquisitionSessionsFromAnotherImageModalComponent } from "@features/image/components/copy-acquisition-sessions-from-another-image-modal/copy-acquisition-sessions-from-another-image-modal.component";
import { ExportAcquisitionsToCsvModalComponent } from "@features/image/components/export-acquisitions-to-csv-modal/export-acquisitions-to-csv-modal.component";
import { ImportAcquisitionsFromCsvFormModalComponent } from "@features/image/components/import-acquisitions-from-csv-form-modal/import-acquisitions-from-csv-form-modal.component";
import {
  AcquisitionForm,
  OverrideAcquisitionFormModalComponent
} from "@features/image/components/override-acquisition-form-modal/override-acquisition-form-modal.component";
import { SaveEquipmentPresetModalComponent } from "@features/image/components/save-equipment-preset-modal/save-equipment-preset-modal.component";
import { ImageEditAcquisitionFieldsService } from "@features/image/services/image-edit-acquisition-fields.service";
import { ImageEditBasicFieldsService } from "@features/image/services/image-edit-basic-fields.service";
import { ImageEditContentFieldsService } from "@features/image/services/image-edit-content-fields.service";
import { ImageEditEquipmentFieldsService } from "@features/image/services/image-edit-equipment-fields.service";
import { ImageEditSettingsFieldsService } from "@features/image/services/image-edit-settings-fields.service";
import { ImageEditThumbnailFieldsService } from "@features/image/services/image-edit-thumbnail-fields.service";
import { ImageEditWatermarkFieldsService } from "@features/image/services/image-edit-watermark-fields.service";
import { ImageEditModelInterface, ImageEditService } from "@features/image/services/image-edit.service";
import { SubjectTypeSuggestionInterface } from "@features/image/services/subject-type-suggestion.interface";
import { SubjectTypeSuggestionService } from "@features/image/services/subject-type-suggestion.service";
import { ImageEditorSetCropperShown } from "@features/image/store/image.actions";
import { NgbModal, NgbModalRef, NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { ConfirmationDialogComponent } from "@shared/components/misc/confirmation-dialog/confirmation-dialog.component";
import { Constants } from "@shared/constants";
import { CookieService } from "ngx-cookie";
import { Observable, forkJoin, of, switchMap, timer, Subscription } from "rxjs";
import { filter, map, take, takeUntil, catchError, timeout } from "rxjs/operators";

@Component({
  selector: "astrobin-image-edit-page",
  templateUrl: "./image-edit-page.component.html",
  styleUrls: ["./image-edit-page.component.scss"]
})
export class ImageEditPageComponent
  extends BaseComponentDirective
  implements OnInit, ComponentCanDeactivate, AfterViewInit, OnDestroy
{
  readonly Constants = Constants;

  readonly isBrowser: boolean;

  ImageAlias = ImageAlias;

  @ViewChild("stepperButtonsTemplate")
  stepperButtonsTemplate: TemplateRef<any>;

  @ViewChild("equipmentStepPreambleTemplate")
  equipmentStepPreambleTemplate: TemplateRef<any>;

  @ViewChild("equipmentStepButtonsTemplate")
  equipmentStepButtonsTemplate: TemplateRef<any>;

  @ViewChild("acquisitionFilterSelectFooterTemplateExtra")
  acquisitionFilterSelectFooterTemplateExtra: TemplateRef<any>;

  @ViewChild("acquisitionAdditionalButtonsTemplate")
  acquisitionAdditionalButtonsTemplate: TemplateRef<any>;

  @ViewChild("presetCreateOffcanvas")
  presetCreateOffcanvas: TemplateRef<any>;

  editingExistingImage: boolean;

  private _returnUrl: string;
  private _subjectTypeSuggestionPollingSubscription: Subscription;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly actions$: Actions,
    public readonly route: ActivatedRoute,
    public readonly router: Router,
    public readonly translateService: TranslateService,
    public readonly classicRoutesService: ClassicRoutesService,
    public readonly titleService: TitleService,
    public readonly remoteSourceAffiliateApiService: RemoteSourceAffiliateApiService,
    public readonly loadingService: LoadingService,
    public readonly windowRefService: WindowRefService,
    public readonly popNotificationsService: PopNotificationsService,
    public readonly imageEditService: ImageEditService,
    public readonly imageEditBasicFieldsService: ImageEditBasicFieldsService,
    public readonly imageEditContentFieldsService: ImageEditContentFieldsService,
    public readonly imageEditWatermarkFieldsService: ImageEditWatermarkFieldsService,
    public readonly imageEditThumbnailFieldsService: ImageEditThumbnailFieldsService,
    public readonly imageEditEquipmentFieldsService: ImageEditEquipmentFieldsService,
    public readonly imageEditAcquisitionFieldsService: ImageEditAcquisitionFieldsService,
    public readonly imageEditSettingsFieldsService: ImageEditSettingsFieldsService,
    public readonly modalService: NgbModal,
    public readonly userService: UserService,
    public readonly cookieService: CookieService,
    public readonly utilsService: UtilsService,
    @Inject(PLATFORM_ID) public readonly platformId: Object,
    public readonly offcanvasService: NgbOffcanvas,
    public readonly deviceService: DeviceService,
    public readonly footerVisibilityService: FooterVisibilityService,
    public readonly imageApiService: ImageApiService,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly subjectTypeSuggestionService: SubjectTypeSuggestionService,
    public readonly imageDataCopyService: ImageDataCopyService
  ) {
    super(store$);

    this.isBrowser = isPlatformBrowser(this.platformId);

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        const state = this.router.getCurrentNavigation()?.extras?.state;
        if (state) {
          this._returnUrl = state["returnUrl"];
        }
      });
  }

  @ViewChild("remoteSourceLabelTemplate")
  set remoteSourceLabelTemplate(template: TemplateRef<any>) {
    this.imageEditService.remoteSourceLabelTemplate = template;
  }

  @ViewChild("remoteSourceOptionTemplate")
  set remoteSourceOptionTemplate(template: TemplateRef<any>) {
    this.imageEditService.remoteSourceOptionTemplate = template;
  }

  @ViewChild("collaboratorLabelTemplate")
  set collaboratorLabelTemplate(template: TemplateRef<any>) {
    this.imageEditBasicFieldsService.collaboratorLabelTemplate = template;
  }

  @ViewChild("collaboratorOptionTemplate")
  set collaboratorOptionTemplate(template: TemplateRef<any>) {
    this.imageEditBasicFieldsService.collaboratorOptionTemplate = template;
  }

  get presets$(): Observable<EquipmentPresetInterface[]> {
    return this.store$.select(selectEquipmentPresets).pipe(takeUntil(this.destroyed$));
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.imageEditService.form.pristine;
  }

  @HostListener("window:keydown", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === "Escape" && this.imageEditEquipmentFieldsService.creationMode) {
      const fullscreenModal =
        isPlatformBrowser(this.platformId) &&
        this.windowRefService.nativeWindow.document.querySelector(".equipment-item-browser-fullscreen-modal");

      // Don't handle Escape if we're in fullscreen mode (let the ItemBrowserComponent handle it)
      if (!fullscreenModal) {
        this.promptToExitEquipmentItemCreationMode();
        event.preventDefault();
      }
    }
  }

  ngOnInit(): void {
    super.ngOnInit();

    // Hide footer on mobile when editing images
    if (this.deviceService.isTouchEnabled() && this.deviceService.smMax() && this.isBrowser) {
      this.footerVisibilityService.hide();
    }

    // Clean up any residual form state from previous edits
    this._cleanupFormArrays();

    // Recreate the form to prevent state leakage between different image edits
    this.imageEditService.form = new FormGroup({});

    const image = this.route.snapshot.data.image;
    this.imageEditService.model = image;

    let overrideAcquisitionForm = null;

    if (image.deepSkyAcquisitions?.length > 0) {
      overrideAcquisitionForm = AcquisitionForm.LONG_EXPOSURE;
    } else if (image.solarSystemAcquisitions?.length > 0) {
      overrideAcquisitionForm = AcquisitionForm.VIDEO_BASED;
    }

    this.imageEditService.model = {
      ...image,
      ...{
        imagingTelescopes2: image.imagingTelescopes2.map(x => x.id),
        imagingCameras2: image.imagingCameras2.map(x => x.id),
        mounts2: image.mounts2.map(x => x.id),
        filters2: image.filters2.map(x => x.id),
        accessories2: image.accessories2.map(x => x.id),
        software2: image.software2.map(x => x.id),
        guidingTelescopes2: image.guidingTelescopes2.map(x => x.id),
        guidingCameras2: image.guidingCameras2.map(x => x.id)
      },
      overrideAcquisitionForm: overrideAcquisitionForm,
      deepSkyAcquisitions: this._cleanDeepSkyAcquisitionDecimals(image.deepSkyAcquisitions),
      solarSystemAcquisitions: this._cleanSolarSystemAcquisitionDecimals(image.solarSystemAcquisitions)
    };

    // Check if we should copy data from a template image
    const copyFromTemplate = this.route.snapshot.queryParams["copyFromTemplate"] === "true";
    if (copyFromTemplate) {
      const templateImage = this.imageDataCopyService.templateImage;
      if (templateImage) {
        const copyableData = this.imageDataCopyService.extractCopyableData(templateImage);

        // Apply the copyable data to the model
        // The extractCopyableData method already returns only the fields we want to copy
        Object.keys(copyableData).forEach(key => {
          if (copyableData[key] !== undefined) {
            (this.imageEditService.model as any)[key] = copyableData[key];
          }
        });

        // Clear the template after use
        this.imageDataCopyService.clearTemplate();
      }
    }

    this.imageEditService.groups = this.route.snapshot.data.groups;
    this.imageEditService.collections = this.route.snapshot.data.collections;
    this.imageEditService.locations = this.route.snapshot.data.locations;

    this.editingExistingImage = !!this.imageEditService.model.subjectType;

    this.titleService.setTitle("Edit image");

    this._initBreadcrumb();

    this.store$.dispatch(new LoadUser({ id: this.imageEditService.model.user }));

    this.store$.dispatch(
      new LoadThumbnail({
        data: { id: this.imageEditService.model.pk, revision: "0", alias: ImageAlias.HD },
        bustCache: false
      })
    );

    this.store$.dispatch(new FindEquipmentPresets({ userId: this.imageEditService.model.user }));

    // Capture the initial equipment preset once presets are loaded
    this.store$
      .select(selectEquipmentPresets)
      .pipe(
        filter(presets => presets !== null && presets.length >= 0),
        take(1)
      )
      .subscribe(() => {
        this.imageEditService.captureInitialEquipmentPreset();
      });

    this.route.fragment.pipe(takeUntil(this.destroyed$)).subscribe((fragment: string) => {
      if (fragment === "3") {
        this.store$.dispatch(new ImageEditorSetCropperShown(true));
      }
    });

    // Start polling for subject type suggestions if image doesn't have a subject type
    if (!this.imageEditService.model.subjectType) {
      this._startSubjectTypeSuggestionPolling();
    }
  }

  ngAfterViewInit(): void {
    this.imageEditAcquisitionFieldsService.acquisitionFilterSelectFooterTemplateExtra =
      this.acquisitionFilterSelectFooterTemplateExtra;
    this.imageEditAcquisitionFieldsService.acquisitionAdditionalButtonsTemplate =
      this.acquisitionAdditionalButtonsTemplate;

    if (this.isBrowser) {
      this.remoteSourceAffiliateApiService
        .getAll()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(remoteSourceAffiliates => {
          // Safeguard: ensure we're still editing the same image
          const currentImagePk = this.route.snapshot.data.image?.pk;
          if (currentImagePk === this.imageEditService.model?.pk) {
            this.imageEditService.remoteSourceAffiliates = remoteSourceAffiliates;
            this._initFields();
          }
        });
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    // Show footer again when leaving the component
    if (this.isBrowser) {
      this.footerVisibilityService.show();
    }

    this._destroyFields();

    // Clean up FormArrays before resetting
    this._cleanupFormArrays();

    // Clean up polling subscription
    if (this._subjectTypeSuggestionPollingSubscription) {
      this._subjectTypeSuggestionPollingSubscription.unsubscribe();
      this.subjectTypeSuggestionService.setPollingStatus(false);
    }

    // Clear critical data from the singleton service to prevent cross-contamination
    // This prevents acquisitions and equipment from leaking between different images
    // when users edit multiple images in the same session
    if (this.imageEditService.model) {
      this.imageEditService.model.deepSkyAcquisitions = [];
      this.imageEditService.model.solarSystemAcquisitions = [];
      // Clear equipment lists to prevent cross-contamination
      this.imageEditService.model.imagingTelescopes2 = [];
      this.imageEditService.model.imagingCameras2 = [];
      this.imageEditService.model.mounts2 = [];
      this.imageEditService.model.filters2 = [];
      this.imageEditService.model.accessories2 = [];
      this.imageEditService.model.software2 = [];
      this.imageEditService.model.guidingTelescopes2 = [];
      this.imageEditService.model.guidingCameras2 = [];
    }
  }

  clearEquipment() {
    this.store$.dispatch(
      new ItemBrowserSet({
        type: EquipmentItemType.TELESCOPE,
        usageType: EquipmentItemUsageType.IMAGING,
        items: [],
        componentId: this.componentId
      })
    );

    this.store$.dispatch(
      new ItemBrowserSet({
        type: EquipmentItemType.CAMERA,
        usageType: EquipmentItemUsageType.IMAGING,
        items: [],
        componentId: this.componentId
      })
    );

    this.store$.dispatch(
      new ItemBrowserSet({
        type: EquipmentItemType.MOUNT,
        items: [],
        componentId: this.componentId
      })
    );

    this.store$.dispatch(
      new ItemBrowserSet({
        type: EquipmentItemType.FILTER,
        items: [],
        componentId: this.componentId
      })
    );

    this.store$.dispatch(
      new ItemBrowserSet({
        type: EquipmentItemType.ACCESSORY,
        items: [],
        componentId: this.componentId
      })
    );

    this.store$.dispatch(
      new ItemBrowserSet({
        type: EquipmentItemType.SOFTWARE,
        items: [],
        componentId: this.componentId
      })
    );

    this.store$.dispatch(
      new ItemBrowserSet({
        type: EquipmentItemType.TELESCOPE,
        usageType: EquipmentItemUsageType.GUIDING,
        items: [],
        componentId: this.componentId
      })
    );

    this.store$.dispatch(
      new ItemBrowserSet({
        type: EquipmentItemType.CAMERA,
        usageType: EquipmentItemUsageType.GUIDING,
        items: [],
        componentId: this.componentId
      })
    );
  }

  onClearEquipmentClicked() {
    if (this.imageEditService.hasEquipmentItems()) {
      this.modalService
        .open(ConfirmationDialogComponent)
        .closed.pipe(take(1))
        .subscribe(() => {
          this.clearEquipment();
        });
    }
  }

  onPresetClicked(preset: EquipmentPresetInterface) {
    this.imageEditService.loadEquipmentPreset(preset, this.componentId).subscribe();
  }

  onSaveEquipmentPresetClicked(cb?: () => void) {
    if (this.imageEditService.hasEquipmentItems()) {
      this.imageEditService
        .currentEquipmentPreset$()
        .pipe(take(1))
        .subscribe(currentEquipmentPreset => {
          const modalRef: NgbModalRef = this.modalService.open(SaveEquipmentPresetModalComponent);
          const componentInstance: SaveEquipmentPresetModalComponent = modalRef.componentInstance;

          const defaultName =
            this.imageEditService.getInitialEquipmentPresetName() ||
            (currentEquipmentPreset ? currentEquipmentPreset.name : "");

          componentInstance.initialPreset = {
            name: defaultName,
            description: "",
            imagingTelescopes: this.imageEditService.model.imagingTelescopes2,
            guidingTelescopes: this.imageEditService.model.guidingTelescopes2,
            imagingCameras: this.imageEditService.model.imagingCameras2,
            guidingCameras: this.imageEditService.model.guidingCameras2,
            mounts: this.imageEditService.model.mounts2,
            filters: this.imageEditService.model.filters2,
            accessories: this.imageEditService.model.accessories2,
            software: this.imageEditService.model.software2
          };

          componentInstance.model.name = defaultName;

          modalRef.closed.pipe(take(1)).subscribe(() => {
            if (!!cb) {
              cb();
            }
          });
        });
    }
  }

  onCopyAcquisitionSessionsFromAnotherImageClicked(event: Event) {
    if (event) {
      event.preventDefault();
    }

    const modalRef: NgbModalRef = this.modalService.open(CopyAcquisitionSessionsFromAnotherImageModalComponent);
    const componentInstance: CopyAcquisitionSessionsFromAnotherImageModalComponent = modalRef.componentInstance;

    componentInstance.alreadyHasAcquisitions =
      this.imageEditService.hasDeepSkyAcquisitions() || this.imageEditService.hasSolarSystemAcquisitions();

    componentInstance.editPageComponentId = this.componentId;
  }

  onExportToCsvClicked(event: Event) {
    if (event) {
      event.preventDefault();
    }

    let acquisitions: any[] = [];
    let headers: string[] = [];

    // Define allowed fields for each acquisition type
    const deepSkyAllowedFields = [
      "date",
      "filter",
      "number",
      "duration",
      "iso",
      "binning",
      "gain",
      "sensorCooling",
      "fNumber",
      "darks",
      "flats",
      "flatDarks",
      "bias",
      "bortle",
      "meanSqm",
      "meanFwhm",
      "temperature"
    ];

    const solarSystemAllowedFields = [
      "date",
      "time",
      "frames",
      "fps",
      "exposurePerFrame",
      "focalLength",
      "iso",
      "gain",
      "seeing",
      "transparency",
      "cmi",
      "cmii",
      "cmiii"
    ];

    if (this.imageEditService.isLongExposure() && this.imageEditService.model.deepSkyAcquisitions?.length > 0) {
      acquisitions = this.imageEditService.model.deepSkyAcquisitions;

      // Only include allowed fields that exist in the data
      headers = deepSkyAllowedFields.filter(field => {
        const key = field === "filter" ? "filter2" : field;
        return acquisitions.some(acq => acq.hasOwnProperty(key) && acq[key] !== null && acq[key] !== undefined);
      });
    } else if (
      !this.imageEditService.isLongExposure() &&
      this.imageEditService.model.solarSystemAcquisitions?.length > 0
    ) {
      acquisitions = this.imageEditService.model.solarSystemAcquisitions;

      // Only include allowed fields that exist in the data
      headers = solarSystemAllowedFields.filter(field =>
        acquisitions.some(acq => acq.hasOwnProperty(field) && acq[field] !== null && acq[field] !== undefined)
      );
    }

    if (acquisitions.length === 0) {
      return;
    }

    // Open the export modal
    const modalRef = this.modalService.open(ExportAcquisitionsToCsvModalComponent, { size: "lg" });
    modalRef.componentInstance.headers = headers;
    modalRef.componentInstance.acquisitions = acquisitions;
  }

  onImportFromCsvClicked(event: Event) {
    if (event) {
      event.preventDefault();
    }

    const _setModel = (acquisitions: any[]): void => {
      if (this.imageEditService.isLongExposure()) {
        this.imageEditService.form.patchValue({ deepSkyAcquisitions: acquisitions });
        this.imageEditService.model = {
          ...this.imageEditService.model,
          deepSkyAcquisitions: acquisitions as DeepSkyAcquisitionInterface[]
        };
      } else {
        this.imageEditService.form.patchValue({ solarSystemAcquisitions: acquisitions });
        this.imageEditService.model = {
          ...this.imageEditService.model,
          solarSystemAcquisitions: acquisitions as SolarSystemAcquisitionInterface[]
        };
      }
    };

    const _fixNumbers = (acquisitions: any[]): void => {
      acquisitions.forEach(acquisition => {
        Object.keys(acquisition).forEach(key => {
          const value = acquisition[key];
          acquisition[key] = UtilsService.isString(value) && UtilsService.isNumeric(value) ? +value : value;
        });
      });
    };

    const _fixFilters = (acquisitions: any[]): void => {
      acquisitions.forEach(acquisition => {
        if (acquisition.filter !== undefined) {
          acquisition["filter2"] = acquisition.filter;
          delete acquisition.filter;
        }
      });
    };

    const _loadFilters$ = (acquisitions: any[]): Observable<FilterInterface[]> => {
      const filterIds: FilterInterface["id"][] = acquisitions
        .filter(acquisition => acquisition["filter2"] !== undefined)
        .map(acquisition => acquisition["filter2"]);

      if (filterIds.length === 0) {
        return of([]);
      } else {
        const observables: Observable<FilterInterface>[] = filterIds.map(id => {
          // Check if filter is already in store first
          return this.store$.select(selectEquipmentItem, { id, type: EquipmentItemType.FILTER }).pipe(
            take(1),
            switchMap(filterFromStore => {
              if (filterFromStore) {
                // Filter is already in store, return it directly
                return of(filterFromStore as FilterInterface);
              } else {
                // Filter not in store, need to load it
                const payload = { type: EquipmentItemType.FILTER, id };

                // Set up observable to wait for the response
                const actionObservable = this.actions$.pipe(
                  ofType(
                    EquipmentActionTypes.LOAD_EQUIPMENT_ITEM_SUCCESS,
                    EquipmentActionTypes.LOAD_EQUIPMENT_ITEM_FAILURE
                  ),
                  map((action: LoadEquipmentItemSuccess | LoadEquipmentItemFailure) => {
                    if (action instanceof LoadEquipmentItemSuccess) {
                      return action.payload.item as FilterInterface;
                    } else {
                      this.popNotificationsService.warning(
                        this.translateService.instant("Filter with id {{id}} could not be found.", {
                          id: action.payload.id
                        })
                      );
                      return null;
                    }
                  }),
                  filter((filter: FilterInterface | null) => {
                    return filter === null || (filter.id === payload.id && filter.klass === payload.type);
                  }),
                  take(1),
                  timeout(10000),
                  catchError(error => {
                    console.warn(`Timeout loading filter ${id}:`, error);
                    this.popNotificationsService.warning(
                      this.translateService.instant("Filter with id {{id}} could not be loaded.", { id })
                    );
                    return of(null);
                  })
                );

                // Dispatch the action
                this.store$.dispatch(new LoadEquipmentItem(payload));

                return actionObservable;
              }
            })
          );
        });

        return forkJoin(observables);
      }
    };

    const startImport = () => {
      const modalRef: NgbModalRef = this.modalService.open(ImportAcquisitionsFromCsvFormModalComponent, { size: "lg" });
      modalRef.closed.subscribe(csv => {
        const acquisitions: any[] = UtilsService.csvToArrayOfDictionaries(csv);

        this.loadingService.setLoading(true);

        // Additional cleanup - remove any empty objects
        const validAcquisitions = acquisitions.filter(acq => Object.keys(acq).length > 0);

        _fixNumbers(validAcquisitions);
        _fixFilters(validAcquisitions);
        _loadFilters$(validAcquisitions).subscribe(filters => {
          filters.forEach(filter => {
            if (filter !== null) {
              this.imageEditService.model.filters2.push(filter.id);
              this.imageEditService.form.patchValue({ filters2: this.imageEditService.model.filters2 });
            }
          });

          _setModel(validAcquisitions);
          this.loadingService.setLoading(false);
        });
      });
    };

    if (this.imageEditService.hasDeepSkyAcquisitions() || this.imageEditService.hasSolarSystemAcquisitions()) {
      const confirmationDialog: NgbModalRef = this.modalService.open(ConfirmationDialogComponent);
      const componentInstance: ConfirmationDialogComponent = confirmationDialog.componentInstance;

      componentInstance.message = this.translateService.instant(
        "You are about to remove all acquisition sessions you have entered thus far."
      );

      confirmationDialog.closed.pipe(take(1)).subscribe(() => {
        startImport();
      });
    } else {
      startImport();
    }
  }

  onOverrideAcquisitionFormClicked(event: Event) {
    if (event) {
      event.preventDefault();
    }

    const modalRef: NgbModalRef = this.modalService.open(OverrideAcquisitionFormModalComponent);
    const componentInstance: OverrideAcquisitionFormModalComponent = modalRef.componentInstance;

    componentInstance.model = {
      acquisitionForm: this.imageEditService.model.overrideAcquisitionForm
    };

    if (!componentInstance.model.acquisitionForm) {
      componentInstance.model = {
        acquisitionForm: this.imageEditService.isDeepSky() ? AcquisitionForm.LONG_EXPOSURE : AcquisitionForm.VIDEO_BASED
      };
    }

    modalRef.closed.subscribe(acquisitionForm => {
      const clearModel = () => {
        this.imageEditService.model = {
          ...this.imageEditService.model,
          deepSkyAcquisitions: [],
          solarSystemAcquisitions: [],
          overrideAcquisitionForm: acquisitionForm
        };
      };
      if (
        this.imageEditService.model.deepSkyAcquisitions?.length > 0 ||
        this.imageEditService.model.solarSystemAcquisitions?.length > 0
      ) {
        const confirmationModalRef: NgbModalRef = this.modalService.open(ConfirmationDialogComponent);
        const confirmationComponentInstance: ConfirmationDialogComponent = confirmationModalRef.componentInstance;
        confirmationComponentInstance.message = this.translateService.instant(
          "Changing this value will remove any acquisition sessions you have already added."
        );
        confirmationModalRef.closed.subscribe(() => {
          clearModel();
        });
      } else {
        clearModel();
      }
    });
  }

  promptToExitEquipmentItemCreationMode(event?: MouseEvent) {
    // Stop event propagation to prevent it from bubbling up in case of nested creation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const modalRef: NgbModalRef = this.modalService.open(ConfirmationDialogComponent);
    const componentInstance: ConfirmationDialogComponent = modalRef.componentInstance;

    componentInstance.title = this.translateService.instant("Exit equipment creation?");
    componentInstance.message = this.translateService.instant("All unsaved changes will be lost.");

    modalRef.closed.pipe(take(1)).subscribe(() => {
      if (isPlatformBrowser(this.platformId)) {
        // Find the cancel button and click it programmatically
        const cancelButton = this.windowRefService.nativeWindow.document.querySelector(
          "#create-new-item .card-footer button.btn-secondary"
        ) as HTMLButtonElement;

        if (cancelButton) {
          cancelButton.click();
        }
      }
    });
  }

  onPresetCreateClicked() {
    this.offcanvasService.open(this.presetCreateOffcanvas, {
      panelClass: "preset-create-offcanvas",
      position: this.deviceService.offcanvasPosition()
    });
  }

  onSave(event: Event) {
    if (event) {
      event.preventDefault();
    }

    if (this.imageEditEquipmentFieldsService.creationMode) {
      this.popNotificationsService.error(
        this.translateService.instant(
          "It looks like you are in the process of creating an equipment item, please complete or cancel that " +
            "operation before saving the entire form."
        ),
        null,
        {
          timeOut: 10000
        }
      );
      return;
    }

    if (!this.imageEditService.form.valid) {
      this.imageEditService.form.markAllAsTouched();
      UtilsService.notifyAboutFieldsWithErrors(
        this.imageEditService.fields,
        this.popNotificationsService,
        this.translateService
      );
      return;
    }

    const _doSave = () => {
      this.store$.dispatch(
        new SaveImage({
          pk: this.imageEditService.model.pk,
          image: { ...this.imageEditService.model, ...this.imageEditService.form.value } as ImageEditModelInterface
        })
      );

      this.actions$.pipe(ofType(AppActionTypes.SAVE_IMAGE_SUCCESS)).subscribe(() => {
        this.imageEditService.form.markAsPristine();

        if (!this._returnUrl) {
          this.currentUserProfile$.pipe(take(1)).subscribe(userProfile => {
            if (userProfile.enableNewGalleryExperience || this.deviceService.isPwa()) {
              void this.router
                .navigate(["i", this.imageEditService.model.hash || this.imageEditService.model.pk])
                .then(() => {
                  this.popNotificationsService.success(this.translateService.instant("Image saved."));
                });
            } else {
              this.loadingService.setLoading(true);
              UtilsService.openLink(
                this.windowRefService.nativeWindow.document,
                this.classicRoutesService.IMAGE(this.imageEditService.model.hash || this.imageEditService.model.pk)
              );
            }
          });
        } else {
          void this.router.navigateByUrl(this._returnUrl).then(() => {
            this.popNotificationsService.success(this.translateService.instant("Image saved."));
          });
        }
      });
    };

    this.imageEditService
      .currentEquipmentPreset$()
      .pipe(take(1))
      .subscribe(preset => {
        if (!preset) {
          const imagingTelescopes = this.imageEditService.model.imagingTelescopes2;
          const imagingCameras = this.imageEditService.model.imagingCameras2;
          const mounts = this.imageEditService.model.mounts2;
          const filters = this.imageEditService.model.filters2;
          const accessories = this.imageEditService.model.accessories2;
          const software = this.imageEditService.model.software2;

          if (
            (!imagingTelescopes.length &&
              !imagingCameras.length &&
              !mounts.length &&
              !filters.length &&
              !accessories.length &&
              !software.length) ||
            !this.imageEditService.isEquipmentDirty()
          ) {
            _doSave();
            return;
          }

          forkJoin([
            ...imagingTelescopes.map(id =>
              this.store$
                .select(selectEquipmentItem, {
                  id,
                  type: EquipmentItemType.TELESCOPE
                })
                .pipe(take(1))
            ),
            ...imagingCameras.map(id =>
              this.store$
                .select(selectEquipmentItem, {
                  id,
                  type: EquipmentItemType.CAMERA
                })
                .pipe(take(1))
            ),
            ...mounts.map(id =>
              this.store$
                .select(selectEquipmentItem, {
                  id,
                  type: EquipmentItemType.MOUNT
                })
                .pipe(take(1))
            ),
            ...filters.map(id =>
              this.store$
                .select(selectEquipmentItem, {
                  id,
                  type: EquipmentItemType.FILTER
                })
                .pipe(take(1))
            ),
            ...accessories.map(id =>
              this.store$
                .select(selectEquipmentItem, {
                  id,
                  type: EquipmentItemType.ACCESSORY
                })
                .pipe(take(1))
            ),
            ...software.map(id =>
              this.store$
                .select(selectEquipmentItem, {
                  id,
                  type: EquipmentItemType.SOFTWARE
                })
                .pipe(take(1))
            )
          ]).subscribe((equipment: EquipmentItem[]) => {
            const modalRef: NgbModalRef = this.modalService.open(ConfirmationDialogComponent);
            const componentInstance: ConfirmationDialogComponent = modalRef.componentInstance;
            componentInstance.title = this.translateService.instant(
              "Would you like to save this equipment configuration as a setup?"
            );
            componentInstance.message = `
            <p>
              ${this.translateService.instant(
                "AstroBin noticed that you have not saved this equipment configuration as a setup. " +
                  "Would you like to save it now, so you can easily load it in the future?"
              )}
            </p>
            <p>
              ${equipment
                .map(
                  equipmentItem =>
                    (equipmentItem.brandName || this.translateService.instant("DIY")) + " " + equipmentItem.name
                )
                .join(" &middot; ")}
            </p>
          `;
            componentInstance.showAreYouSure = false;
            componentInstance.cancelLabel = this.translateService.instant("No, just save the image");
            componentInstance.confirmLabel = this.translateService.instant("Yes, save the setup");

            modalRef.closed.subscribe(result => {
              this.onSaveEquipmentPresetClicked(() => {
                _doSave();
              });
            });

            modalRef.dismissed.subscribe(() => {
              _doSave();
            });
          });
        } else {
          _doSave();
        }
      });
  }

  private _initFields(): void {
    // Safety check - ensure model exists before initializing fields
    if (!this.imageEditService.model) {
      return;
    }

    this.store$
      .select(selectUser, this.imageEditService.model.user)
      .pipe(
        filter(user => !!user),
        take(1),
        switchMap(user =>
          this.currentUser$.pipe(
            take(1),
            map(currentUser => ({ user, currentUser }))
          )
        )
      )
      .subscribe(({ user, currentUser }) => {
        const basic = {
          id: "image-stepper-basic-information",
          props: { label: this.translateService.instant("Basic information") },
          fieldGroup: [
            this.imageEditBasicFieldsService.getTitleField(),
            this.imageEditBasicFieldsService.getDescriptionField(),
            this.imageEditBasicFieldsService.getPendingCollaboratorsField(),
            this.imageEditBasicFieldsService.getLinkField(),
            this.imageEditBasicFieldsService.getLinkToFitsField()
          ]
        };

        const content = {
          id: "image-stepper-content",
          props: { label: this.translateService.instant("Content") },
          fieldGroup: [
            this.imageEditContentFieldsService.getAcquisitionTypeField(),
            this.imageEditContentFieldsService.getSubjectTypeField(),
            this.imageEditContentFieldsService.getSolarSystemMainSubjectField(),
            this.imageEditContentFieldsService.getDataSourceField(),
            this.imageEditContentFieldsService.getRemoteSourceField()
          ]
        };

        if (this.imageEditService.model.user === currentUser.id) {
          content.fieldGroup.push(this.imageEditContentFieldsService.getLocationsField());
        }

        content.fieldGroup.push(
          this.imageEditContentFieldsService.getGroupsField(),
          this.imageEditContentFieldsService.getCollectionsField()
        );

        const thumbnail = {
          id: "image-stepper-thumbnail",
          props: { label: this.translateService.instant("Thumbnail") },
          fieldGroup: [
            this.imageEditThumbnailFieldsService.getThumbnailField(),
            this.imageEditThumbnailFieldsService.getSharpenThumbnailsField()
          ]
        };

        const watermark = {
          id: "image-stepper-watermark",
          props: { label: this.translateService.instant("Watermark") },
          fieldGroup: [
            this.imageEditWatermarkFieldsService.getWatermarkCheckboxField(),
            this.imageEditWatermarkFieldsService.getWatermarkTextField(),
            this.imageEditWatermarkFieldsService.getWatermarkPosition(),
            this.imageEditWatermarkFieldsService.getWatermarkTextSize(),
            this.imageEditWatermarkFieldsService.getWatermarkTextOpacity()
          ]
        };

        const equipment = {
          id: "image-stepper-equipment",
          props: {
            label: this.translateService.instant("Equipment"),
            stepPreambleTemplate: this.equipmentStepPreambleTemplate,
            stepActionsTemplate: this.equipmentStepButtonsTemplate
          },
          fieldGroup: [
            this.imageEditEquipmentFieldsService.getImagingTelescopes(this.componentId),
            this.imageEditEquipmentFieldsService.getImagingCameras(this.componentId),
            this.imageEditEquipmentFieldsService.getMounts(this.componentId),
            this.imageEditEquipmentFieldsService.getFilters(this.componentId),
            this.imageEditEquipmentFieldsService.getAccessories(this.componentId),
            this.imageEditEquipmentFieldsService.getSoftware(this.componentId),
            this.imageEditEquipmentFieldsService.getShowGuidingEquipment(),
            this.imageEditEquipmentFieldsService.getGuidingTelescopes(this.componentId),
            this.imageEditEquipmentFieldsService.getGuidingCameras(this.componentId)
          ]
        };

        const acquisition = {
          id: "image-stepper-acquisition",
          props: {
            label: this.translateService.instant("Acquisition")
          },
          fieldGroup: this.imageEditAcquisitionFieldsService.getFields()
        };

        const settings = {
          id: "image-stepper-settings",
          props: { label: this.translateService.instant("Settings") },
          fieldGroup: []
        };

        if (this.imageEditService.model.videoFile) {
          settings.fieldGroup = [
            this.imageEditSettingsFieldsService.getLicenseField(),
            this.imageEditSettingsFieldsService.getKeyValueTagsField(),
            this.imageEditSettingsFieldsService.getLoopVideoField(),
            this.imageEditSettingsFieldsService.getDownloadLimitationField(),
            this.imageEditSettingsFieldsService.getAllowCommentsField(),
            this.imageEditSettingsFieldsService.getAllowImageAdjustmentsWidgetField()
          ];
        } else {
          settings.fieldGroup = [
            this.imageEditSettingsFieldsService.getLicenseField(),
            this.imageEditSettingsFieldsService.getMouseHoverImageField(),
            this.imageEditSettingsFieldsService.getKeyValueTagsField(),
            this.imageEditSettingsFieldsService.getFullSizeDisplayLimitationField(),
            this.imageEditSettingsFieldsService.getMaxZoomField(),
            this.imageEditSettingsFieldsService.getDownloadLimitationField(),
            this.imageEditSettingsFieldsService.getAllowCommentsField(),
            this.imageEditSettingsFieldsService.getAllowImageAdjustmentsWidgetField()
          ];
        }

        const fieldGroup = [basic, content, thumbnail, watermark];

        if (this.userService.isInGroup(user, Constants.OWN_EQUIPMENT_MIGRATORS_GROUP)) {
          fieldGroup.push(equipment);
        }

        fieldGroup.push(acquisition);
        fieldGroup.push(settings);

        this.imageEditService.fields = [
          {
            type: "stepper",
            id: "image-stepper-field",
            props: {
              buttonsTemplate: this.stepperButtonsTemplate,
              fixed: true
            },
            fieldGroup
          }
        ];

        // Wait for formly to process the fields by queuing the initialization
        // after the current execution context and Angular's change detection
        void Promise.resolve().then(() => {
          // Double-check that fields are actually initialized
          if (this.imageEditService.fields && this.imageEditService.fields.length > 0) {
            this.imageEditBasicFieldsService.onFieldsInitialized();
            this.imageEditContentFieldsService.onFieldsInitialized();
            this.imageEditWatermarkFieldsService.onFieldsInitialized();
            this.imageEditThumbnailFieldsService.onFieldsInitialized();
            this.imageEditEquipmentFieldsService.onFieldsInitialized();
            this.imageEditAcquisitionFieldsService.onFieldsInitialized();
            this.imageEditSettingsFieldsService.onFieldsInitialized();
          }
        });
      });
  }

  private _destroyFields(): void {
    this.imageEditService.fields = [];
  }

  private _initBreadcrumb(): void {
    this.store$.dispatch(
      new SetBreadcrumb({
        breadcrumb: [
          {
            label: this.translateService.instant("Image")
          },
          {
            label: this.imageEditService.model.title
          },
          {
            label: this.translateService.instant("Edit")
          }
        ]
      })
    );
  }

  private _cleanDeepSkyAcquisitionDecimals(acquisitions: DeepSkyAcquisitionInterface[]): DeepSkyAcquisitionInterface[] {
    if (!acquisitions) {
      return acquisitions;
    }

    return acquisitions.map(acquisition => ({
      ...acquisition,
      duration:
        acquisition.duration !== null && acquisition.duration !== undefined
          ? parseFloat(acquisition.duration).toString()
          : acquisition.duration
    }));
  }

  private _cleanSolarSystemAcquisitionDecimals(
    acquisitions: SolarSystemAcquisitionInterface[]
  ): SolarSystemAcquisitionInterface[] {
    // No string decimal fields in SolarSystemAcquisitionInterface
    return acquisitions;
  }

  private _cleanupFormArrays(): void {
    // Clear FormArray controls to prevent data persistence between edits
    const deepSkyControl = this.imageEditService.form.get("deepSkyAcquisitions");
    const solarSystemControl = this.imageEditService.form.get("solarSystemAcquisitions");

    if (deepSkyControl && deepSkyControl instanceof FormArray) {
      // Clear all controls from the FormArray
      deepSkyControl.clear();
    }

    if (solarSystemControl && solarSystemControl instanceof FormArray) {
      // Clear all controls from the FormArray
      solarSystemControl.clear();
    }

    // Reset the entire form to clear any dirty state
    this.imageEditService.form.reset();
  }

  private _startSubjectTypeSuggestionPolling(): void {
    const POLL_INTERVAL = 5000; // 5 seconds
    const MAX_ATTEMPTS = 60; // 5 minutes total
    let attempts = 0;

    // Mark that polling is active
    this.subjectTypeSuggestionService.setPollingStatus(true);

    this.subjectTypeSuggestionService.updateSubjectTypeSuggestionState({
      status: "polling",
      suggestion: null,
      error: null,
      appliedSuggestion: false,
      dismissed: false
    });

    this._subjectTypeSuggestionPollingSubscription = timer(0, POLL_INTERVAL)
      .pipe(
        takeUntil(this.destroyed$),
        switchMap(() => {
          attempts++;
          return this.imageApiService.getSubjectTypeSuggestion(this.imageEditService.model.pk).pipe(
            catchError(error => {
              console.error("Error fetching subject type suggestion:", error);
              return of({ status: "error" as const, suggestion: null, error: error.message });
            })
          );
        })
      )
      .subscribe(response => {
        // Handle processing/pending states - keep showing "Analyzing..." pill
        if (response.status === "processing" || response.status === "pending") {
          this.subjectTypeSuggestionService.updateSubjectTypeSuggestionState({
            status: "polling", // Keep status as polling to show loading pill
            suggestion: null,
            error: null,
            appliedSuggestion: false,
            dismissed: false
          });
          // Continue polling
          return;
        }

        if (response.status === "completed" && response.suggestion) {
          const shouldAutoApply = this._shouldAutoApplySuggestion(response.suggestion);
          const shouldShowSuggestion = this._shouldShowSuggestion(response.suggestion);

          // Auto-apply if rules pass and field is empty
          if (shouldAutoApply && !this.imageEditService.form.get("subjectType").value) {
            // First update the model to ensure the solar system field becomes visible
            this.imageEditService.model.subjectType = response.suggestion.subjectType;
            this.imageEditService.form.patchValue({ subjectType: response.suggestion.subjectType });

            this.subjectTypeSuggestionService.updateSubjectTypeSuggestionState({
              status: "completed",
              suggestion: response.suggestion,
              error: null,
              appliedSuggestion: true,
              dismissed: false // Keep false to show info message
            });

            // Also auto-apply solar system main subject if applicable
            if (response.suggestion.subjectType === "SOLAR_SYSTEM" && response.suggestion.solarSystemMainSubject) {
              // Need a small delay to ensure the field is visible and initialized
              this.utilsService.delay(100).subscribe(() => {
                this.imageEditService.model.solarSystemMainSubject = response.suggestion.solarSystemMainSubject;
                this.imageEditService.form.patchValue({
                  solarSystemMainSubject: response.suggestion.solarSystemMainSubject
                });

                this.subjectTypeSuggestionService.updateSolarSystemSuggestionState({
                  status: "completed",
                  suggestion: response.suggestion,
                  error: null,
                  appliedSuggestion: true,
                  dismissed: false // Keep false to show info message
                });

                // Trigger change detection
                this.changeDetectorRef.markForCheck();
              });
            }
          } else if (shouldShowSuggestion) {
            // Show as suggestion pill if confidence meets threshold
            this.subjectTypeSuggestionService.updateSubjectTypeSuggestionState({
              status: "completed",
              suggestion: response.suggestion,
              error: null,
              appliedSuggestion: false,
              dismissed: false
            });

            // Also set solar system suggestion if applicable
            if (response.suggestion.subjectType === "SOLAR_SYSTEM" && response.suggestion.solarSystemMainSubject) {
              this.subjectTypeSuggestionService.updateSolarSystemSuggestionState({
                status: "completed",
                suggestion: response.suggestion,
                error: null,
                appliedSuggestion: false,
                dismissed: false
              });
            }
          } else {
            // Confidence too low, don't show to user
            this.subjectTypeSuggestionService.updateSubjectTypeSuggestionState({
              status: "completed",
              suggestion: null,
              error: null,
              appliedSuggestion: false,
              dismissed: true
            });
          }

          // Stop polling when we have a result
          if (this._subjectTypeSuggestionPollingSubscription) {
            this._subjectTypeSuggestionPollingSubscription.unsubscribe();
            this.subjectTypeSuggestionService.setPollingStatus(false);
          }
        } else if (response.status === "error") {
          this.subjectTypeSuggestionService.updateSubjectTypeSuggestionState({
            status: "error",
            suggestion: null,
            error: response.error,
            appliedSuggestion: false,
            dismissed: false
          });

          // Stop polling on error
          if (this._subjectTypeSuggestionPollingSubscription) {
            this._subjectTypeSuggestionPollingSubscription.unsubscribe();
            this.subjectTypeSuggestionService.setPollingStatus(false);
          }
        } else if (response.status === "not_needed") {
          // User already set a value, stop polling
          this.subjectTypeSuggestionService.updateSubjectTypeSuggestionState({
            status: "not_needed",
            suggestion: null,
            error: null,
            appliedSuggestion: false,
            dismissed: false
          });

          if (this._subjectTypeSuggestionPollingSubscription) {
            this._subjectTypeSuggestionPollingSubscription.unsubscribe();
            this.subjectTypeSuggestionService.setPollingStatus(false);
          }
        } else if (attempts >= MAX_ATTEMPTS) {
          // Timeout after max attempts
          this.subjectTypeSuggestionService.updateSubjectTypeSuggestionState({
            status: "error",
            suggestion: null,
            error: "Suggestion generation timed out",
            appliedSuggestion: false,
            dismissed: false
          });

          if (this._subjectTypeSuggestionPollingSubscription) {
            this._subjectTypeSuggestionPollingSubscription.unsubscribe();
            this.subjectTypeSuggestionService.setPollingStatus(false);
          }
        }
        // Continue polling if status is "processing" or "pending"
      });
  }

  onApplySubjectTypeSuggestion(value: string): void {
    this.imageEditService.form.patchValue({ subjectType: value });
    const currentState = this.subjectTypeSuggestionService.subjectTypeSuggestionState;
    this.subjectTypeSuggestionService.updateSubjectTypeSuggestionState({
      ...currentState,
      appliedSuggestion: true,
      dismissed: true
    });
  }

  onDismissSubjectTypeSuggestion(): void {
    const currentState = this.subjectTypeSuggestionService.subjectTypeSuggestionState;
    this.subjectTypeSuggestionService.updateSubjectTypeSuggestionState({
      ...currentState,
      dismissed: true
    });
  }

  onApplySolarSystemSuggestion(value: string): void {
    this.imageEditService.form.patchValue({ solarSystemMainSubject: value });
    const currentState = this.subjectTypeSuggestionService.solarSystemSuggestionState;
    this.subjectTypeSuggestionService.updateSolarSystemSuggestionState({
      ...currentState,
      appliedSuggestion: true,
      dismissed: true
    });
  }

  onDismissSolarSystemSuggestion(): void {
    const currentState = this.subjectTypeSuggestionService.solarSystemSuggestionState;
    this.subjectTypeSuggestionService.updateSolarSystemSuggestionState({
      ...currentState,
      dismissed: true
    });
  }

  private _shouldAutoApplySuggestion(suggestion: SubjectTypeSuggestionInterface): boolean {
    // Categories that performed well (80%+ accuracy) - safe to auto-apply at high confidence
    const safeCategories = [
      SubjectType.DEEP_SKY, // 100% accuracy at high confidence
      SubjectType.SOLAR_SYSTEM, // 100% accuracy
      SubjectType.NORTHERN_LIGHTS, // 90% accuracy
      SubjectType.GEAR, // 90% accuracy
      SubjectType.STAR_TRAILS // 80% accuracy
    ];

    const safeSolarSystemSubjects = [
      SolarSystemSubjectType.SUN, // 100% accuracy
      SolarSystemSubjectType.VENUS, // 100% accuracy
      SolarSystemSubjectType.COMET, // 100% accuracy
      SolarSystemSubjectType.MOON, // 90% accuracy
      SolarSystemSubjectType.MARS, // 90% accuracy
      SolarSystemSubjectType.JUPITER, // 80% accuracy
      SolarSystemSubjectType.SATURN // 80% accuracy
    ];

    // Problematic categories - never auto-apply even at high confidence
    const neverAutoApply = [
      SubjectType.OTHER, // 0% accuracy
      SubjectType.WIDE_FIELD, // 50% accuracy
      SubjectType.LANDSCAPE // 50% accuracy
    ];

    const neverAutoApplySolarSystem = [
      SolarSystemSubjectType.MERCURY, // 60% accuracy
      SolarSystemSubjectType.URANUS, // 70% accuracy - ice giants are challenging
      SolarSystemSubjectType.NEPTUNE, // Similar to Uranus
      SolarSystemSubjectType.OTHER
    ];

    // Check if it's a problematic category
    if (neverAutoApply.includes(suggestion.subjectType)) {
      return false;
    }

    if (suggestion.solarSystemMainSubject && neverAutoApplySolarSystem.includes(suggestion.solarSystemMainSubject)) {
      return false;
    }

    // For safe categories, auto-apply at ≥90% confidence
    if (safeCategories.includes(suggestion.subjectType)) {
      // For solar system, also check the specific subject is safe
      if (suggestion.subjectType === SubjectType.SOLAR_SYSTEM && suggestion.solarSystemMainSubject) {
        return suggestion.confidence >= 0.9 && safeSolarSystemSubjects.includes(suggestion.solarSystemMainSubject);
      }
      return suggestion.confidence >= 0.9;
    }

    // For ARTIFICIAL_SATELLITE (70% accuracy), require even higher confidence
    if (suggestion.subjectType === SubjectType.ARTIFICIAL_SATELLITE) {
      return false; // Never auto-apply
    }

    // Default: don't auto-apply
    return false;
  }

  private _shouldShowSuggestion(suggestion: SubjectTypeSuggestionInterface): boolean {
    // Show suggestions at ≥85% confidence
    // This gives users the option to apply them manually
    return suggestion.confidence >= 0.85;
  }

  get hasAcquisitions(): boolean {
    return (
      this.imageEditService.model.deepSkyAcquisitions?.length > 0 ||
      this.imageEditService.model.solarSystemAcquisitions?.length > 0
    );
  }
}
